import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import SkillQuiz from '@/components/SkillQuiz';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import ProfileCompletionCard from '@/components/dashboard/ProfileCompletionCard';
import NotificationsCard from '@/components/dashboard/NotificationsCard';
import SkillTestsCard from '@/components/dashboard/SkillTestsCard';
import SkillTestResultsCard from '@/components/dashboard/SkillTestResultsCard';
import DashboardLoader from '@/components/dashboard/DashboardLoader';
import ProfileSummaryCard from '@/components/dashboard/ProfileSummaryCard';
import JobRecommendationsCard from '@/components/dashboard/JobRecommendationsCard';
import JobInterestsCard from '@/components/dashboard/JobInterestsCard';
import { Bot } from 'lucide-react';
import AccessRequestCard from '@/components/dashboard/AccessRequestCard';

const JobSeekerDashboard = () => {
  const { user, profile, isLoading, signOut } = useAuth();
  const { toast } = useToast();
  const [profileCompletion, setProfileCompletion] = useState(30);
  const [notifications, setNotifications] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [skills, setSkills] = useState([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [userSkills, setUserSkills] = useState([]);
  const [isLoadingUserSkills, setIsLoadingUserSkills] = useState(true);

  useEffect(() => {
    if (user) {
      fetchProfileCompletion();
      fetchNotifications();
      fetchQuizAttempts();
      fetchAvailableSkills();
      fetchUserSkills();
    }
  }, [user]);

  const fetchUserSkills = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoadingUserSkills(true);
      const { data, error } = await supabase
        .from('job_seeker_skills')
        .select('skill_name, proficiency_level, years_of_experience')
        .eq('job_seeker_id', user.id);

      if (error) throw error;
      setUserSkills(data || []);
    } catch (error) {
      console.error('Error fetching user skills:', error.message);
    } finally {
      setIsLoadingUserSkills(false);
    }
  };

  const fetchAvailableSkills = async () => {
    try {
      setIsLoadingSkills(true);
      const { data, error } = await supabase
        .from('quiz_config')
        .select('skill');
      
      if (error) throw error;
      
      if (data) {
        const uniqueSkills = [...new Set(data.map(item => item.skill))];
        setSkills(uniqueSkills);
      }
    } catch (error) {
      console.error('Error fetching skills:', error.message);
      toast({
        title: "Error",
        description: "Failed to load available skills for quizzes.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSkills(false);
    }
  };

  const fetchProfileCompletion = async () => {
    try {
      const { data, error } = await supabase
        .from('job_seeker_profiles')
        .select('profile_completion')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      if (data && data.profile_completion !== null) {
        setProfileCompletion(data.profile_completion);
      }
    } catch (error) {
      console.error('Error fetching profile completion:', error.message);
    }
  };

  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error.message);
    }
  };

  const fetchQuizAttempts = async () => {
    try {
      const { data, error } = await supabase
        .from('quiz_attempts')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      setQuizAttempts(data || []);
    } catch (error) {
      console.error('Error fetching quiz attempts:', error.message);
    }
  };

  const handleStartQuiz = (skill) => {
    setSelectedSkill(skill);
    setShowQuiz(true);
  };

  const handleQuizComplete = () => {
    setShowQuiz(false);
    fetchQuizAttempts();
    toast({
      title: "Quiz completed",
      description: "Your quiz results have been saved.",
    });
  };

  const refreshJobFeeds = async () => {
    try {
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;
      
      if (!token) {
        console.error('No authentication token available');
        return;
      }
      
      const response = await fetch('https://rrsgsfpxfwcnlttxnlom.supabase.co/functions/v1/fetch-job-feeds', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh job feeds');
      }
      
      const data = await response.json();
      toast({
        title: "Job feeds refreshed",
        description: `Processed ${data.results.length} feeds successfully.`,
      });
    } catch (error) {
      console.error('Error refreshing job feeds:', error.message);
      toast({
        title: "Error",
        description: "Failed to refresh job feeds. Please try again later.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <DashboardLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (profile?.role !== 'job_seeker') {
    return <Navigate to="/onboarding" />;
  }

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE] p-6">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => setShowQuiz(false)} 
            variant="outline" 
            className="mb-6 border-[#0EA5E9]/20 text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
          >
            Back to Dashboard
          </Button>
          <SkillQuiz skill={selectedSkill} onComplete={handleQuizComplete} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <DashboardHeader 
          firstName={profile?.first_name} 
          onSignOut={signOut} 
        />

        <ProfileCompletionCard profileCompletion={profileCompletion} />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProfileSummaryCard userId={user.id} />
          <JobRecommendationsCard />
        </div>

        <div className="flex flex-wrap gap-6 mb-6">
          <Link to="/agent-builder">
            <Button 
              variant="outline" 
              className="border-[#0EA5E9]/20 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 flex items-center gap-2"
            >
              <Bot className="h-4 w-4" />
              SOP Agent Builder
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {notifications.length > 0 && (
            <NotificationsCard notifications={notifications} />
          )}
          
          {!isLoadingSkills && skills.length > 0 && (
            <SkillTestsCard 
              skills={skills} 
              quizAttempts={quizAttempts} 
              onStartQuiz={handleStartQuiz} 
            />
          )}
        </div>

        <JobInterestsCard />

        <SkillTestResultsCard quizAttempts={quizAttempts} />

        {!profile?.agent_builder_access && (
          <AccessRequestCard 
            featureName="AI Agent Builder" 
            requestType="agent_builder" 
            userId={user.id} 
          />
        )}
      </div>
    </div>
  );
};

export default JobSeekerDashboard;
